/**
 * ATL-1319 — why a till refused to complete a sale, as one normalized event.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS EXISTS TO ANSWER
 *
 * Paid flight ADS-2026-001 closed at its D7 gate with 4 attributed installs → 3 signups → 3
 * organizations → 3 first products → **0 first sales**. The PostHog path audit showed all three
 * users opening New Sale (13 views between them), two of them opening AddCartItem and one opening
 * EditCartItem — and then nothing. Checkout itself is demonstrably fine at scale (149 distinct
 * users emitted `sale_created` in the week to 2026-09-14).
 *
 * So the question is which refusal they hit, and the answer did not exist: every branch in the New
 * Sale path that refuses does so by `setSaveError(...)` or a toast and an early `return`, emitting
 * nothing. "They opened the screen and wandered off" and "the till refused them thirteen times for
 * something we could have fixed in an afternoon" produced byte-identical event streams. This module
 * is the difference between those two.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE PAYLOAD IS CLOSED, AND THAT IS THE POINT
 *
 * `captureCheckoutBlocked` builds the property bag itself and ignores everything else the caller
 * hands it. It is not defensive style for its own sake: a checkout refusal is the one moment in
 * this app where the tempting context — the cart, the customer, the amount, the cashier's typed
 * comment — is exactly the context that may not leave the device. A call site that "just adds the
 * total for debugging" is one line and passes review; making it structurally impossible is cheaper
 * than policing it forever across twenty call sites. Same reason `reason`, `screen` and `stage` are
 * all coerced to a known value rather than forwarded: an unrecognised reason becomes `unknown`,
 * never free text.
 *
 * The organization and person context is deliberately NOT built here. AppStack already calls
 * `postHog.group('organization', …)` and `postHog.register(…)`, so every capture on the device
 * carries the tenant and the environment. Re-attaching them here would be a second, divergent
 * source of the same facts.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ANALYTICS IS NEVER LOAD-BEARING (ATL-901)
 *
 * One unguarded telemetry dereference once locked a paying customer out of their till on every
 * platform for four hours. Everything here tolerates a missing, partial or throwing PostHog client
 * and returns a value instead of propagating. This code runs at the moment a cashier is already
 * being refused a sale; an exception thrown from the reporter would turn a refusal they could fix
 * into a crash they cannot.
 */

/** The ONE event name. Documented in `docs/technical/analytics-checkout-blocked.md`. */
export const CHECKOUT_BLOCKED_EVENT = 'sale_checkout_blocked'

/**
 * Every reason the New Sale path can refuse, normalized.
 *
 * One list, exported, because a reason that is spelled at the call site is a reason that drifts:
 * this repo has already lost a whole collection from its parity tooling to `'writeoffs'` vs
 * `'writeOffs'` (ATL-723), and a PostHog breakdown splits just as silently as a Firestore query
 * returns zero documents.
 *
 * ⚠️ `FISCAL_OFD_ERROR` IS RESERVED AND IS NOT EMITTED TODAY. The ticket's enum names it, and it is
 * kept here so the contract is stable if a blocking fiscal path is ever added — but as the code
 * stands, fiscalization happens AFTER `saveNewSale` has durably committed the sale and is not
 * awaited (see `saveSaleToOFD` / `reportFiscalizationFailure` in NewSaleScreen). A receipt that the
 * OFD refused is a sale that EXISTS and that already emitted `sale_created`. Emitting
 * `sale_checkout_blocked` for it would put completed sales into the refusal funnel, which is the
 * one thing this event must never do. Unfiscalized sales have their own instrument: the persistent
 * banner, the Sales History retry path, and Sentry.
 */
export const CHECKOUT_BLOCKED_REASONS = Object.freeze({
  /**
   * The cart holds nothing to sell. Since ADS-FIX-2026-001 every checkout CTA is disabled on an
   * empty cart (`isCartEmptyForCheckout`), so this should be ≈0 — a non-zero count is a new path.
   */
  CART_EMPTY: 'cart_empty',
  /** No shift is open and this role may not sell without one. */
  SHIFT_NOT_OPEN: 'shift_not_open',
  /** No purchase method (the `constants/purchaseMethods` selection) is chosen. */
  PURCHASE_METHOD_MISSING: 'purchase_method_missing',
  /** No payment method is chosen, and the sale is not on account. */
  PAYMENT_METHOD_MISSING: 'payment_method_missing',
  /** A mixed payment with no second method. */
  SECOND_PAYMENT_METHOD_MISSING: 'second_payment_method_missing',
  /** A mixed payment whose two halves name the same method. */
  SECOND_PAYMENT_METHOD_DUPLICATE: 'second_payment_method_duplicate',
  /** The first half of a mixed payment is empty or unreadable as a number. */
  INVALID_PAYMENT_AMOUNT: 'invalid_payment_amount',
  /** The discount box holds something that is not a number. */
  INVALID_DISCOUNT_AMOUNT: 'invalid_discount_amount',
  /** A quantity that is absent, unreadable, or not greater than zero. */
  INVALID_QUANTITY: 'invalid_quantity',
  /** A price or purchase price that is absent or unreadable. */
  PRICE_MISSING: 'price_missing',
  /** A price or purchase price that is readable but negative. */
  INVALID_PRICE: 'invalid_price',
  /** Stock is at or below zero and this role may not sell out of stock. */
  STOCK_UNAVAILABLE: 'stock_unavailable',
  /** The delivery price box holds something that is not a number. */
  INVALID_DELIVERY_PRICE: 'invalid_delivery_price',
  /** A delivery the merchant pays for, with no wallet to pay it from. */
  DELIVERY_PAYMENT_SOURCE_MISSING: 'delivery_payment_source_missing',
  /** A delivery with neither an address nor a client carrying one. */
  DELIVERY_ADDRESS_MISSING: 'delivery_address_missing',
  /** The write refused because a referenced product is incomplete (a missing ingredient). */
  MISSING_PRODUCT_FIELDS: 'missing_product_fields',
  /** The write never reached a server. */
  NETWORK_ERROR: 'network_error',
  /** A server answered and refused. */
  SERVER_REFUSED: 'server_refused',
  /** RESERVED — see the warning above. Not emitted by any call site today. */
  FISCAL_OFD_ERROR: 'fiscal_ofd_error',
  /** A refusal we could not place. Non-zero counts here are a bug in the classifier, not noise. */
  UNKNOWN: 'unknown',
})

/**
 * ADS-FIX-2026-001 — the ONE predicate for "there is nothing in the cart to check out".
 *
 * It is read in two places that must never disagree: the checkout CTAs on NewSaleScreen and in
 * PaymentView (which are DISABLED while it is true, with an «add a product» hint beside them), and
 * the `cart_empty` guard at the top of `submit()`. Before this fix the CTAs were always live, so an
 * empty-cart tap reached the guard and was refused — 98 events, 46% of every `sale_checkout_blocked`
 * in 2026-09-22…28 — a dead end the cashier kept walking into, which also drowned the refusal
 * signal the event exists to measure.
 *
 * The guard and its `cart_empty` emission are KEPT on purpose. No UI path reaches `submit()` with
 * an empty cart any more, so the guard is what still refuses if one ever does, and a non-zero
 * `cart_empty` count after this release is that path announcing itself rather than noise.
 *
 * Only an EMPTY cart counts. A cart whose lines all have quantity 0 is not empty and stays enabled:
 * `submit()` refuses it as `invalid_quantity`, which is a different problem with a different fix.
 */
export function isCartEmptyForCheckout(cartItems: readonly unknown[] | null | undefined): boolean {
  return !cartItems || cartItems.length === 0
}

export type CheckoutBlockedReason =
  (typeof CHECKOUT_BLOCKED_REASONS)[keyof typeof CHECKOUT_BLOCKED_REASONS]

/** The same set as a flat list, for validation and for the contract test. */
export const CHECKOUT_BLOCKED_REASON_VALUES: readonly CheckoutBlockedReason[] = Object.freeze(
  Object.values(CHECKOUT_BLOCKED_REASONS),
) as readonly CheckoutBlockedReason[]

/**
 * Which screen refused. Both are part of one checkout; see `CHECKOUT_BLOCKED_STAGES`.
 *
 * ⚠️ It names the screen that OWNS the refusing code, which is not always the screen the cashier is
 * looking at. EditCartItem's modifier picker calls `addNewCartItem` — NewSaleScreen's callback,
 * handed over through route params — so a modifier refused for stock reports
 * `screen: 'NewSaleScreen'` from on top of EditCartItem. That is left as it is rather than threaded
 * through an override: the refusing guard, the cart being added to, and the fix all live on New
 * Sale, and an extra parameter on the app's hottest callback is a poor trade for a label. It is
 * written down here and in the docs because it is the kind of thing that is otherwise rediscovered
 * as a bug.
 */
export const CHECKOUT_BLOCKED_SCREENS = Object.freeze({
  NEW_SALE: 'NewSaleScreen',
  EDIT_CART_ITEM: 'EditCartItemScreen',
})

export type CheckoutBlockedScreen =
  (typeof CHECKOUT_BLOCKED_SCREENS)[keyof typeof CHECKOUT_BLOCKED_SCREENS]

const SCREEN_VALUES: readonly string[] = Object.values(CHECKOUT_BLOCKED_SCREENS)

/**
 * WHERE in the checkout the refusal happened.
 *
 * This is the one property beyond the ticket's literal `{reason, screen}`, and it is here because
 * without it the event cannot answer the question it was built for. Two refusals carry the same
 * reason at different stages — `stock_unavailable` when a product will not go INTO the cart, and
 * `stock_unavailable` when a built cart will not check out — and they call for opposite fixes. The
 * flight's own audit is the case in point: two of three users reached AddCartItem and one reached
 * EditCartItem, so the population most likely never pressed Done at all, and a submit-only
 * instrument would have reported a clean zero and taught us nothing.
 *
 * `screen` cannot stand in for it: both cart-stage refusals in `addNewCartItem` and every
 * checkout-stage refusal in `submit` happen on `NewSaleScreen`.
 *
 * A bounded two-value enum, so it carries no more information about the merchant than `reason`.
 */
export const CHECKOUT_BLOCKED_STAGES = Object.freeze({
  /** Building the cart: adding a line, or editing one. No sale has been attempted yet. */
  CART: 'cart',
  /** `submit()`: the cashier asked for the sale and did not get it. */
  CHECKOUT: 'checkout',
})

export type CheckoutBlockedStage =
  (typeof CHECKOUT_BLOCKED_STAGES)[keyof typeof CHECKOUT_BLOCKED_STAGES]

const STAGE_VALUES: readonly string[] = Object.values(CHECKOUT_BLOCKED_STAGES)

/**
 * The exact, closed property bag. Nothing else is ever sent.
 *
 * A `type` rather than an `interface` on purpose: only a type alias gets TypeScript's implicit
 * index signature, which is what lets it be handed to a `capture(event, properties)` that is
 * declared to take a `Record`. An interface here is a compile error at the call below.
 */
export type CheckoutBlockedProperties = {
  reason: CheckoutBlockedReason
  screen: CheckoutBlockedScreen
  stage: CheckoutBlockedStage
}

export interface CheckoutBlockedOutcome {
  captured: boolean
  event: typeof CHECKOUT_BLOCKED_EVENT
  properties: CheckoutBlockedProperties
}

/** The narrow slice of the PostHog client this module uses. Everything about it is optional. */
export interface CheckoutBlockedPostHog {
  capture?: (event: string, properties?: Record<string, unknown>) => unknown
}

export interface CheckoutBlockedOptions {
  screen?: string | null
  stage?: string | null
  // Deliberately no index signature: extra keys are a type error at the call site, and are
  // discarded at runtime for the JS call sites the compiler does not see.
}

function normalizeReason(reason: unknown): CheckoutBlockedReason {
  return typeof reason === 'string' &&
    (CHECKOUT_BLOCKED_REASON_VALUES as readonly string[]).includes(reason)
    ? (reason as CheckoutBlockedReason)
    : CHECKOUT_BLOCKED_REASONS.UNKNOWN
}

function normalizeScreen(screen: unknown): CheckoutBlockedScreen {
  return typeof screen === 'string' && SCREEN_VALUES.includes(screen)
    ? (screen as CheckoutBlockedScreen)
    : CHECKOUT_BLOCKED_SCREENS.NEW_SALE
}

function normalizeStage(stage: unknown): CheckoutBlockedStage {
  return typeof stage === 'string' && STAGE_VALUES.includes(stage)
    ? (stage as CheckoutBlockedStage)
    : CHECKOUT_BLOCKED_STAGES.CHECKOUT
}

/**
 * Report that the checkout refused, and say why.
 *
 * Returns what it sent rather than nothing, so a call site can be asserted on in a test without
 * reaching into the PostHog mock — and so a caller that wants to log the same fact has one source.
 *
 * @param postHog the client from `usePostHog()`; may be null, partial, or broken
 * @param reason one of `CHECKOUT_BLOCKED_REASONS`; anything else becomes `unknown`
 */
export function captureCheckoutBlocked(
  postHog: CheckoutBlockedPostHog | null | undefined,
  reason: string,
  options?: CheckoutBlockedOptions,
): CheckoutBlockedOutcome {
  // Built before the client is even looked at, so the returned outcome describes the same payload
  // whether or not the capture succeeded.
  const properties: CheckoutBlockedProperties = {
    reason: normalizeReason(reason),
    screen: normalizeScreen(options?.screen),
    stage: normalizeStage(options?.stage),
  }
  try {
    if (typeof postHog?.capture !== 'function') {
      return { captured: false, event: CHECKOUT_BLOCKED_EVENT, properties }
    }
    postHog.capture(CHECKOUT_BLOCKED_EVENT, properties)
    return { captured: true, event: CHECKOUT_BLOCKED_EVENT, properties }
  } catch (error) {
    // Swallowed on purpose, and only here. A till must keep selling when analytics is broken; the
    // console keeps the fault visible to anyone actually looking at a device.
    console.warn('[analytics] sale_checkout_blocked could not be captured; checkout continues', error)
    return { captured: false, event: CHECKOUT_BLOCKED_EVENT, properties }
  }
}

/**
 * Firestore/gRPC codes that mean the request never got an answer.
 *
 * Together with `REFUSAL_ERROR_CODES` below this enumerates every canonical gRPC status except two,
 * rather than just the codes seen so far. That near-completeness is what makes the `unknown`
 * promise below worth anything: a bucket that fills up with ordinary server codes on day one is a
 * bucket everyone learns to ignore, and the one real signal in it is then invisible.
 *
 * The two absences are deliberate. `ok` (0) is not an error. `unknown` (2) is gRPC's own "the
 * server failed in a way this client cannot classify" — mapping it to `server_refused` would assert
 * something the transport itself declined to assert, so it falls through to reason `unknown`, which
 * is what it means. It is therefore the one SERVER-side failure that legitimately lands there.
 */
const TRANSPORT_ERROR_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ECONNRESET',
  'ETIMEDOUT',
  'ENOTFOUND',
  'unavailable',
  'deadline-exceeded',
  // The till gave up on the request; no server ever answered it.
  'cancelled',
  'network-request-failed',
  'auth/network-request-failed',
])

/** Firestore/gRPC codes that mean a server considered the write and said no. */
const REFUSAL_ERROR_CODES = new Set([
  'permission-denied',
  'invalid-argument',
  'failed-precondition',
  'resource-exhausted',
  'unauthenticated',
  'already-exists',
  'out-of-range',
  'aborted',
  'internal',
  'data-loss',
  'unimplemented',
  'not-found',
])

function errorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}

function httpStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null
  const candidate = error as { status?: unknown; response?: { status?: unknown } }
  const status = candidate.response?.status ?? candidate.status
  return typeof status === 'number' ? status : null
}

function errorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) return ''
  const message = (error as { message?: unknown }).message
  return typeof message === 'string' ? message : ''
}

/**
 * Place a `saveNewSale` failure into one of the refusal buckets.
 *
 * The dual-write makes this genuinely ambiguous, so the order below is the whole design:
 *
 *  1. `INGREDIENT_NOT_FOUND` first — the one failure the MERCHANT can fix, and the one that would
 *     otherwise be aggregated with backend outages and never looked at.
 *  2. An HTTP status next, BEFORE the transport code. axios attaches `code: 'ERR_BAD_REQUEST'`
 *     alongside a 400, so reading the code first would file a contract break under "bad wifi" —
 *     and this repo has already paid for a refusal that was invisible because it looked like
 *     flakiness (ATL-908: five distinct causes all arriving as a bare 401).
 *  3. Then the transport codes, then a last-resort look at the message.
 *
 * The error TEXT never leaves this function. It is read only to decide a bucket, because a thrown
 * message can carry a product name, a client name, or an address.
 */
export function classifySaveFailure(error: unknown): CheckoutBlockedReason {
  const message = errorMessage(error)
  if (message === 'INGREDIENT_NOT_FOUND') return CHECKOUT_BLOCKED_REASONS.MISSING_PRODUCT_FIELDS

  const status = httpStatus(error)
  if (status !== null && status >= 400) return CHECKOUT_BLOCKED_REASONS.SERVER_REFUSED

  const code = errorCode(error)
  if (code !== null) {
    if (TRANSPORT_ERROR_CODES.has(code)) return CHECKOUT_BLOCKED_REASONS.NETWORK_ERROR
    if (REFUSAL_ERROR_CODES.has(code)) return CHECKOUT_BLOCKED_REASONS.SERVER_REFUSED
  }

  if (/network\s*error|network request failed|timeout|timed out/i.test(message)) {
    return CHECKOUT_BLOCKED_REASONS.NETWORK_ERROR
  }

  return CHECKOUT_BLOCKED_REASONS.UNKNOWN
}
